<script>
import ChefCard from '@/components/chef/ChefCard'
import GoogleAddressAutocomplete from '@/components/GoogleAddressAutocomplete'
import googleMapsApi from '@/mixins/googleMapsApi'
import { mapMutations } from 'vuex'

import feelingItems from '@/assets/feeling-options'
import postalCodes from '@/assets/postal-codes'

import { openingHours, allowedHours } from '@/config'
const [start, end] = openingHours

import { SEARCH_CHEFS_QUERY } from '@/graph/gql/chefs'

export default {
  name: 'HomeView',
  components: { GoogleAddressAutocomplete, ChefCard },
  mixins: [googleMapsApi],
  apollo: {
    chefs: {
      query: SEARCH_CHEFS_QUERY,
      deep: true,
      variables() {
        const to = this.deliveryAddress
        const on = { date: this.deliveryDate, hour: this.deliveryHour }

        return {
          delivery: { on, to },
          productsFirst: 1,
          productsDate: this.$moment(on.date).toDate()
        }
      },
      skip() {
        const { deliveryAddress, deliveryHour } = this
        return !deliveryAddress.name || !deliveryHour || !this.deliveryAvailable
      },
      result({ data }) {
        this.error = !data.chefs
      },
      error(error) {
        console.error(error)
        this.error = true
      }
    }
  },
  data() {
    const { clamp, get } = this.lodash

    return {
      feelingItems,
      form: {
        feeling: 'COMFORT'
      },
      prefillAddress: null,
      datePickerModal: false,
      error: false
    }
  },
  computed: {
    allowedHours() {
      const tomorrow = this.$moment().add(1, 'day')
      const hour = tomorrow.hour()

      return this.$moment(this.deliveryDate).isSame(tomorrow, 'day')
        ? allowedHours.filter(x => x.value > hour)
        : allowedHours
    },

    firstAvailableTime() {
      const hour = this.$moment().hour() + 1
      const time = allowedHours.find(x => x.value >= hour)

      return time
    },

    firstAvailableDay() {
      const weekday = this.$moment().isoWeekday()
      const date = this.$moment().isoWeekday(
        weekday < 3 && this.firstAvailableTime !== undefined ? 3 : 10
      ) //get next Wednesday

      return date
    },

    minDate() {
      return this.firstAvailableDay.format('YYYY-MM-DD')
    },

    maxDate() {
      return this.firstAvailableDay.add(2, 'week').format('YYYY-MM-DD')
    },

    deliveryAddress: {
      get() {
        return this.$store.state.cart.delivery.address
      },
      set(value) {
        this.setDeliveryAddress(value)
      }
    },

    deliveryDate: {
      get() {
        return this.$store.state.cart.delivery.date
      },
      set(value) {
        this.setDeliveryDate(value)
      }
    },

    deliveryHour: {
      get() {
        return this.$store.state.cart.delivery.hour
      },
      set(value) {
        this.setDeliveryHour(value)
      }
    },

    formattedDeliveryDate() {
      return this.$moment(this.deliveryDate).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd Do',
        sameElse: 'dddd Do'
      })
    },

    deliveryAvailable() {
      if (!this.deliveryAddress) return null
      const { postal_code } = this.deliveryAddress
      return postal_code && postalCodes.includes(postal_code)
    }
  },
  created() {
    if (!this.deliveryAddress.name) {
      this.setDeliveryAddress({ name: '', place_id: '' })
    }

    if (!this.deliveryDate) {
      this.setDeliveryDate(this.firstAvailableDay.format('YYYY-MM-DD'))
    }

    if (!this.deliveryHour) {
      const { clamp, get } = this.lodash
      this.setDeliveryHour(
        clamp(get(this.firstAvailableTime, 'value', 0), start, end)
      )
    }
  },
  methods: {
    ...mapMutations('cart', [
      'setDeliveryAddress',
      'setDeliveryDate',
      'setDeliveryHour'
    ]),

    checkAllowedDate(date) {
      return this.$moment(date).isoWeekday() === 3
    },

    tryAgain() {
      if (this.mapsApiLoadingFailed) this.$forceUpdate()
      if (this.error) this.$apollo.queries.chefs.refresh()
    }
  }
}
</script>

<template>
  <div>
    <div class="home-top-menu d-lg-none">
      <v-btn icon @click="$emit('openDrawer')">
        <v-icon color="black" size="18">$vuetify.icons.menu</v-icon>
      </v-btn>
    </div>

    <!-- Toolbar -->
    <v-app-bar
      flat
      fixed
      color="white"
      inverted-scroll
      :scroll-threshold="100"
      app
    >
      <v-btn icon class="d-lg-none" @click="$emit('openDrawer')">
        <v-icon color="black" size="18">$vuetify.icons.menu</v-icon>
      </v-btn>
      <v-spacer />
    </v-app-bar>

    <transition name="slide-up">
      <!-- Chef view page -->
      <v-container>
        <div class="px-2">
          <div class="buyer-home-view">
            <div>
              <!-- Select delivery date -->
              <div
                class="display-1 mb-2 font-weight-bold accent--text"
                @click="datePickerModal = !datePickerModal"
              >
                {{ formattedDeliveryDate }}
                <v-icon color="accent" size="16">
                  $vuetify.icons.caretDown
                </v-icon>
              </div>
              <v-dialog ref="dialog" v-model="datePickerModal" max-width="290">
                <v-date-picker
                  v-model="deliveryDate"
                  color="primary"
                  header-color="primary"
                  :show-current="false"
                  :allowed-dates="checkAllowedDate"
                  :min="minDate"
                  :max="maxDate"
                  @input="datePickerModal = false"
                />
              </v-dialog>

              <v-menu max-width="200">
                <template v-slot:activator="{ on }">
                  <div class="title primary--text" v-on="on">
                    {{ allowedHours.find(x => x.value == deliveryHour).label }}
                    <v-icon color="primary" size="14">
                      $vuetify.icons.caretDown
                    </v-icon>
                  </div>
                </template>
                <v-list>
                  <v-list-item
                    v-for="(item, index) in allowedHours"
                    :key="index"
                    @click="deliveryHour = item.value"
                  >
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

              <div class="d-flex">
                <!-- Select location -->
                <GoogleAddressAutocomplete
                  v-model="deliveryAddress"
                  placeholder="Deliver to"
                  :prefill="prefillAddress"
                  prepend-inner-icon="$vuetify.icons.compass"
                />
              </div>

              <!-- Select feelings -->
              <div v-if="false" class="mb-8">
                <div class="caption grey--text mb-2">Craving</div>

                <div class="feeling-select">
                  <div class="feeling-options d-flex pl-5 pr-4">
                    <v-chip
                      v-for="feeling in feelingItems"
                      :key="feeling.value"
                      :outlined="form.feeling !== feeling.value"
                      :color="form.feeling === feeling.value ? 'primary' : ''"
                      class="mr-1"
                      :ripple="false"
                      @click="form.feeling = feeling.value"
                    >
                      <v-icon v-if="feeling.icon" left>{{
                        feeling.icon
                      }}</v-icon>
                      {{ feeling.text }}
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div
                v-if="mapsApiLoadingFailed || error"
                class="mt-4 search-empty"
              >
                <div>
                  <h1 class="subtitle-1 font-weight-bold mb-0">
                    Oops!
                  </h1>
                  <p class="body-2 mb-6">
                    Something went wrong. Please try <br />
                    again in a few minutes.
                  </p>

                  <v-btn
                    rounded
                    outlined
                    depressed
                    color="primary"
                    @click="tryAgain"
                  >
                    <v-icon
                      size="15"
                      color="primary"
                      left
                      v-text="'$vuetify.icons.redo'"
                    />
                    Try again
                  </v-btn>
                </div>
              </div>

              <div v-if="deliveryAvailable === false" class="search-empty">
                <div>
                  <h1 class="subtitle-1 font-weight-bold">
                    We're not there yet
                  </h1>
                  <p class="body-2">
                    Should we email you when we <br />
                    launch in your location?
                  </p>

                  <v-btn rounded depressed color="primary">
                    Sign me up
                  </v-btn>
                </div>

                <div class="px-5 pt-3 empty-illustration">
                  <v-img src="@/assets/master_delivery.svg" />
                </div>
              </div>

              <div v-if="deliveryAddress.name === ''" class="search-empty">
                <div>
                  <h1 class="subtitle-1 font-weight-bold">
                    Find home cooks near you
                  </h1>
                  <p class="body-2">
                    We need GPS access to show cooks<br />
                    that deliver to your location.
                  </p>

                  <v-btn
                    rounded
                    depressed
                    color="primary"
                    @click="prefillAddress = true"
                  >
                    Share my location
                  </v-btn>
                </div>

                <div class="px-5 pt-3 empty-illustration">
                  <v-img src="@/assets/master_delivery.svg" />
                </div>
              </div>

              <div
                v-else-if="!$apollo.loading && chefs && !chefs.length"
                class="search-empty"
              >
                <div>
                  <h1 class="subtitle-1 font-weight-bold">
                    Gone like hotcakes
                  </h1>
                  <p class="body-2">
                    All the cooks are sold out! <br />
                    Try another date?
                  </p>
                </div>

                <div class="px-5 pt-3 empty-illustration">
                  <v-img src="@/assets/master_delivery.svg" />
                </div>
              </div>

              <div v-else>
                <v-container fluid grid-list-lg class="pa-0">
                  <v-layout row wrap>
                    <!-- Loading -->
                    <v-skeleton-loader
                      v-if="$apollo.loading"
                      class="chef-card-loader"
                      type="image"
                      width="100vw"
                      height="100vw"
                    />

                    <!-- Chefs list -->
                    <v-flex
                      v-for="chef in chefs"
                      v-else
                      :key="chef.id"
                      xs12
                      md6
                      lg4
                    >
                      <chef-card :chef="chef" />
                    </v-flex>
                  </v-layout>
                </v-container>
              </div>
            </div>
          </div>
        </div>
      </v-container>
    </transition>
  </div>
</template>

<style lang="sass">
.home-top-menu
  position: absolute
  left: 10px
  top: 10px

.buyer-home-view
  margin-top: 46px

.address-autocomplete, .date-input, .hour-input
  .v-input__icon--prepend-inner .v-icon
    font-size: 16px
    color: black !important
  .v-label--active
    left: -28px !important
.hour-input
  width: 140px
  .v-input__append-inner
    display: none
  .v-select__selections
    flex-wrap: nowrap
    input
      width: 1px
  .v-select__selection
    max-width: 100%
    width: 100%

.feeling-select
  margin-left: -20px
  overflow-x: auto
  -ms-overflow-style: none
  @media only screen and (max-device-width: 960px)
    width: 100vw
  &::-webkit-scrollbar
    display: none
  .feeling-options
    width: fit-content
  .v-chip
    font-size: 12px
    flex-shrink: 0
    .v-icon
      font-size: 12px
      &.v-icon--left
        margin-left: -2px
        margin-right: 8px

.buyer-home-view
  .chef-card-loader
    .v-skeleton-loader__image
      height: 100%

.empty-illustration
  position: absolute
  bottom: 25px
  right: 0
  width: 80%
  max-width: 500px

.search-empty
  text-align: center
  padding-top: 15%
  height: 100%
  min-height: 440px
</style>
